<script>
import CreateLeadModal from './CreateLeadModal.vue';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    CreateLeadModal,
  },
  props: {
    show: Boolean,
    onClose: { type: Function, required: true },
    isLoading: Boolean,
    personData: { type: Object, default: () => ({}) },
    leadList: { type: Array, default: () => [] },
    tenantData: { type: Object, default: () => ({}) },
    multiLeadsPersonUrl: { type: String, default: '' },
    showCloseButton: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false },
    size: { type: String, default: '' },
    modalType: { type: String, default: 'centered' },
    contactId: { type: Number, default: null },
    inboxId: { type: Number, default: null },
    conversationId: { type: Number, default: null },
  },
  emits: ['leadCreated'],
  data() {
    return {
      mousedDownOnBackdrop: false,
      showCreateModal: false,
    };
  },
  computed: {
    modalClassName() {
      return `modal-mask skip-context-menu ${
        this.modalType === 'right-aligned' ? 'right-aligned' : ''
      }`;
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscape);
    document.body.addEventListener('mouseup', this.onMouseUp);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscape);
    document.body.removeEventListener('mouseup', this.onMouseUp);
  },
  methods: {
    handleMouseDown() {
      this.mousedDownOnBackdrop = true;
    },
    handleContentMouseDown(event) {
      event.stopPropagation();
    },
    close() {
      this.onClose();
    },
    onMouseUp() {
      if (this.mousedDownOnBackdrop) {
        this.mousedDownOnBackdrop = false;
        this.close();
      }
    },
    handleEscape(e) {
      if (this.show && e.code === 'Escape') {
        this.close();
      }
    },
    getTemperatureColor(temp) {
      return { 3: '#ffcfcf', 2: '#fff3b0', 1: '#cfe5ff' }[temp] || '#f2f2f2';
    },
    getTextColor(temp) {
      return temp >= 1 && temp <= 3 ? '#000000' : '#4a4a4a';
    },
    getTemperatureLabel(temp) {
      switch (temp) {
        case 3:
          return this.$t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.HOT');
        case 2:
          return this.$t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.WARM');
        case 1:
          return this.$t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.COLD');
        default:
          return this.$t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.NONE');
      }
    },
    getLeadUrl(id) {
      const domain =
        this.tenantData &&
        this.tenantData.domains &&
        this.tenantData.domains[0] &&
        this.tenantData.domains[0].domain
          ? this.tenantData.domains[0].domain
          : '';
      if (!domain) return '#';
      const baseUrl = domain.includes('localhost')
        ? `http://${domain}:8000`
        : `https://${domain}`;
      return `${baseUrl}/admin/leads/view/${id}`;
    },
    openCreateLeadModal() {
      this.showCreateModal = true;
    },
    closeCreateLeadModal() {
      this.showCreateModal = false;
    },
    handleLeadCreated() {
      this.closeCreateLeadModal();
      this.$emit('leadCreated');
      emitter.emit(
        'newToastMessage',
        this.$t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.ANNOUNCEMENT.SUCCESS')
      );
    },
    handleCreationError(errorMessage) {
      this.closeCreateLeadModal();
      const message =
        errorMessage ||
        this.$t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.ANNOUNCEMENT.FAIL');
      emitter.emit('newToastMessage', message);
    },
    truncateText(text, maxLength) {
      if (!text) return '-';
      if (text.length <= maxLength) {
        return text;
      }
      return text.slice(0, maxLength) + '...';
    },
  },
};
</script>

<template>
  <div>
    <transition name="modal-fade">
      <div v-if="show" :class="modalClassName" @mousedown="handleMouseDown">
        <div
          class="modal-container rtl:text-right shadow-md max-h-full overflow-auto relative bg-white dark:bg-slate-800 skip-context-menu"
          :class="{
            'rounded-xl w-[37.5rem]': !fullWidth,
            'items-center rounded-none flex h-full justify-center w-full':
              fullWidth,
            [size]: true,
          }"
          @mousedown.stop="handleContentMouseDown"
        >
          <woot-button
            v-if="showCloseButton"
            color-scheme="secondary"
            icon="dismiss"
            variant="clear"
            class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
            @click="close"
          />
          <div class="w-full">
            <div
              class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700"
            >
              <h2 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ $t('CONVERSATION.MULTILEADS.TITLE') }}
              </h2>
            </div>

            <div class="p-4 max-h-[70vh] overflow-y-auto">
              <div
                v-if="isLoading"
                class="text-center text-gray-500 dark:text-gray-400"
              >
                {{ $t('CONVERSATION.MULTILEADS.LOADING') }}
              </div>

              <div v-else-if="personData || leadList.length" class="space-y-4">
                <div
                  class="space-y-2 text-sm text-slate-700 dark:text-slate-200"
                >
                  <div>
                    <strong>{{ $t('CONVERSATION.MULTILEADS.NAME') }}</strong>
                    {{ personData && personData.name ? personData.name : '-' }}
                  </div>
                  <div>
                    <strong>{{ $t('CONVERSATION.MULTILEADS.EMAIL') }}</strong>
                    {{
                      personData &&
                      personData.emails &&
                      personData.emails[0] &&
                      personData.emails[0].value
                        ? personData.emails[0].value
                        : '-'
                    }}
                  </div>

                  <a
                    v-if="multiLeadsPersonUrl"
                    :href="multiLeadsPersonUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <woot-button
                      variant="link"
                      icon="open"
                      size="small"
                      color-scheme="primary"
                    >
                      {{ $t('CONVERSATION.MULTILEADS.MULTI_LEADS_ACCESS') }}
                    </woot-button>
                  </a>
                </div>

                <hr class="my-2 border-gray-300 dark:border-slate-700" />

                <h3
                  class="text-lg font-semibold text-slate-900 dark:text-white"
                >
                  Leads:
                </h3>
                <div v-if="leadList.length">
                  <div
                    class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
                  >
                    <div
                      v-for="lead in leadList"
                      :key="lead.id"
                      class="relative p-3 border rounded-lg shadow-sm transition-transform duration-200 ease-in-out hover:-translate-y-1 hover:shadow-md"
                      :style="{
                        backgroundColor: getTemperatureColor(lead.temperature),
                        color: getTextColor(lead.temperature),
                      }"
                    >
                      <div class="absolute top-2 left-2 text-xl opacity-80">
                        <span v-if="lead.temperature === 3">🔥</span>
                        <span v-else-if="lead.temperature === 2">🌤️</span>
                        <span v-else-if="lead.temperature === 1">❄️</span>
                        <span v-else>⚪</span>
                      </div>
                      <div
                        class="absolute top-0 right-0 w-6 h-6 bg-white"
                        style="clip-path: polygon(100% 0, 0% 0, 100% 100%)"
                      />
                      <br />
                      <div
                        class="font-semibold text-base mt-2 truncate"
                        :title="lead.title"
                      >
                        {{ lead.title }}
                      </div>
                      <div class="text-sm">
                        <strong>{{
                          $t('CONVERSATION.MULTILEADS.TEMPERATURE')
                        }}</strong>
                        {{ getTemperatureLabel(lead.temperature) }}
                      </div>
                      <div class="text-sm">
                        <strong>Status:</strong> {{ lead.status }}
                      </div>
                      <div
                        v-if="lead.lead_value && Number(lead.lead_value) !== 0"
                        class="text-sm"
                      >
                        <strong>{{
                          $t('CONVERSATION.MULTILEADS.VALUE')
                        }}</strong>
                        {{ lead.lead_value }}
                      </div>
                      <div class="text-sm">
                        <strong>{{
                          $t('CONVERSATION.MULTILEADS.DESCRIPTION')
                        }}</strong>
                        {{ truncateText(lead.description, 30) }}
                      </div>
                      <div class="mt-2">
                        <a
                          :href="getLeadUrl(lead.id)"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <woot-button
                            variant="link"
                            icon="open"
                            size="small"
                            color-scheme="primary"
                          >
                            {{
                              $t('CONVERSATION.MULTILEADS.MULTI_LEADS_ACCESS')
                            }}
                          </woot-button>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="text-center text-gray-500 dark:text-gray-400">
                {{ $t('CONVERSATION.MULTILEADS.NOT_FOUND') }}
              </div>
              <div class="text-right mt-6">
                <woot-button @click="openCreateLeadModal">
                  {{ $t('CONVERSATION.MULTILEADS.CREATE_LEAD_BUTTON') }}
                </woot-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <CreateLeadModal
      v-if="showCreateModal"
      :show="showCreateModal"
      :contact-id="contactId"
      :inbox-id="inboxId"
      :tenant-data="tenantData"
      :conversation-id="conversationId"
      @close="closeCreateLeadModal"
      @lead-created="handleLeadCreated"
      @creation-error="handleCreationError"
    />
  </div>
</template>

<style lang="scss" scoped>
.modal-mask {
  @apply flex items-center justify-center bg-modal-backdrop-light dark:bg-modal-backdrop-dark z-[9990] h-full left-0 fixed top-0 w-full;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  @apply opacity-0;
}
</style>
